import axios from "axios";

// A Send contractsapi is still running after its own wait: the draft stays
// locked to the file that went out, and the editor says to check back.
export class SendStillRunningError extends Error {
  constructor() {
    super("The proposal is still being sent");
    this.name = "SendStillRunningError";
  }
}

// The stable codes a refused Send carries: the draft's own checks, and the
// e-sign connector's answers as contractsapi passes them on. Every other code,
// and a failure with none, keeps the generic message.
export enum ProposalSendErrorCode {
  NO_ESIGN_CONNECTION = "NO_ESIGN_CONNECTION",
  // The rep has no seat in the account's PandaDoc workspace.
  SENDER_NOT_MATCHED = "SENDER_NOT_MATCHED",
  // Someone saved the proposal after this edit began.
  STALE_DRAFT = "STALE_DRAFT",
  // The deal's terms changed since the draft was saved.
  OUT_OF_DATE = "OUT_OF_DATE",
  NO_SIGNER = "NO_SIGNER",
  // A field the rep types is still blank; the refusal's message names it.
  NEEDS_INPUT = "NEEDS_INPUT",
  // The document holds characters its font can't print.
  UNSUPPORTED_TEXT = "UNSUPPORTED_TEXT",
  // Another Send for this draft, or it was withdrawn meanwhile.
  INVALID_REQUEST = "INVALID_REQUEST",
  NOT_A_DRAFT = "NOT_A_DRAFT",
  DEAL_CLOSED = "DEAL_CLOSED",
  VENDOR_UNAVAILABLE = "VENDOR_UNAVAILABLE",
  // The e-sign connector isn't answering.
  ESIGN_UNAVAILABLE = "ESIGN_UNAVAILABLE",
  // A Send still running, from another tab or after this editor gave up asking.
  SEND_IN_PROGRESS = "SEND_IN_PROGRESS"
}

const KEY_PREFIX = "contracts.proposals.editor.sendErrors";

const MESSAGE_KEY_BY_CODE: Record<ProposalSendErrorCode, string> = {
  [ProposalSendErrorCode.NO_ESIGN_CONNECTION]: `${KEY_PREFIX}.noConnection`,
  [ProposalSendErrorCode.SENDER_NOT_MATCHED]: `${KEY_PREFIX}.senderNotMatched`,
  [ProposalSendErrorCode.STALE_DRAFT]: `${KEY_PREFIX}.stale`,
  [ProposalSendErrorCode.OUT_OF_DATE]: `${KEY_PREFIX}.outOfDate`,
  [ProposalSendErrorCode.NO_SIGNER]: `${KEY_PREFIX}.noSigner`,
  [ProposalSendErrorCode.NEEDS_INPUT]: `${KEY_PREFIX}.needsInput`,
  [ProposalSendErrorCode.UNSUPPORTED_TEXT]: `${KEY_PREFIX}.unsupportedText`,
  [ProposalSendErrorCode.INVALID_REQUEST]: `${KEY_PREFIX}.changed`,
  [ProposalSendErrorCode.NOT_A_DRAFT]: `${KEY_PREFIX}.notADraft`,
  [ProposalSendErrorCode.DEAL_CLOSED]: `${KEY_PREFIX}.dealClosed`,
  [ProposalSendErrorCode.VENDOR_UNAVAILABLE]: `${KEY_PREFIX}.busy`,
  [ProposalSendErrorCode.ESIGN_UNAVAILABLE]: `${KEY_PREFIX}.busy`,
  [ProposalSendErrorCode.SEND_IN_PROGRESS]: `${KEY_PREFIX}.stillSending`
};

const GENERIC_KEY = `${KEY_PREFIX}.failed`;

// Own-property only: `in` would accept inherited names like "toString".
const isKnownCode = (code: unknown): code is ProposalSendErrorCode =>
  typeof code === "string" &&
  Object.prototype.hasOwnProperty.call(MESSAGE_KEY_BY_CODE, code);

const refusalCode = (error: unknown): unknown => {
  if (error instanceof SendStillRunningError) {
    return ProposalSendErrorCode.SEND_IN_PROGRESS;
  }
  return axios.isAxiosError(error)
    ? (error.response?.data as { code?: unknown } | undefined)?.code
    : undefined;
};

// The i18n key for a Send that didn't go out, read from the refusal's `code`.
// As for a save, the server's own `message` is not shown, but for blanks below.
export const sendErrorMessageKey = (error: unknown): string => {
  const code = refusalCode(error);
  return isKnownCode(code) ? MESSAGE_KEY_BY_CODE[code] : GENERIC_KEY;
};

// A refusal for blank fields names them ("Fill in Valid until before sending."),
// which no fixed wording can, so its own message is shown when it has one.
export const blankFieldsMessage = (error: unknown): string | undefined => {
  if (refusalCode(error) !== ProposalSendErrorCode.NEEDS_INPUT)
    return undefined;
  const message = axios.isAxiosError(error)
    ? (error.response?.data as { message?: unknown } | undefined)?.message
    : undefined;
  return typeof message === "string" && message.trim() ? message : undefined;
};

// The rep has no PandaDoc seat: shown as a modal rather than the inline reason.
export const isSenderNotMatched = (error: unknown) =>
  refusalCode(error) === ProposalSendErrorCode.SENDER_NOT_MATCHED;

// The Send started and is still going: the draft stays locked to its file.
export const isSendStillRunning = (error: unknown) =>
  refusalCode(error) === ProposalSendErrorCode.SEND_IN_PROGRESS;
